import { useContext, useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { StaxLogo } from "./Logo";
import { openContact } from "./ContactDialog";
import { ContentCtx } from "@/lib/content";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "The Work", href: "#work" },
  { label: "Brand Kit", href: "#brand-kit" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const content = useContext(ContentCtx);
  const links = LINKS.filter((l) => l.href !== "#work" || !!content?.sections?.work);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 border-b-4 border-ink transition-colors ${scrolled ? "bg-paper" : "bg-paper/90 backdrop-blur"}`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8">
          <a href="#top" data-testid="nav-logo-link" aria-label="STAX Marketing home">
            <StaxLogo size={46} />
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono2 text-xs font-bold uppercase tracking-[0.18em] text-ink hover:text-staxmagenta transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-button"
              onClick={() => openContact()}
              className="hidden sm:inline-flex items-center gap-1.5 border-2 border-ink bg-staxcyan px-4 py-2.5 font-display uppercase text-sm text-ink shadow-hard-sm transition-transform hover:-translate-y-0.5"
            >
              Start a Project <ArrowUpRight size={16} />
            </button>
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden border-2 border-ink bg-staxyellow p-2"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[60] bg-ink text-white flex flex-col">
          <div className="flex h-[72px] items-center justify-between border-b-4 border-white/20 px-4">
            <StaxLogo dark size={40} />
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu"
              className="border-2 border-white p-2 hover:bg-staxmagenta transition-colors">
              <X size={20} />
            </button>
          </div>
          <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
            {links.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="font-display uppercase text-4xl sm:text-5xl hover:text-staxyellow transition-colors"
                style={{ transitionDelay: `${i * 20}ms` }}
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-nav-cta"
              onClick={() => { setOpen(false); openContact(); }}
              className="mt-8 inline-flex w-fit items-center gap-2 border-2 border-white bg-staxmagenta px-6 py-4 font-display uppercase text-xl"
            >
              Start a Project <ArrowUpRight size={22} />
            </button>
          </nav>
        </div>
      )}
    </>
  );
};
